import { useState, useEffect, useCallback, useRef } from 'react';
import { parseCSV, buildInventoryAssetRows } from '../utils/csvParser';
import { SHEET_CONFIG, getSheetCsvUrl } from '../constants';

export const useInventoryData = () => {
  const [data, setData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const isFetchingRef = useRef(false);
  const isMountedRef = useRef(true);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;

    try {
      if (!silent) setRefreshing(true);

      setError('');

      const response = await fetch(
        getSheetCsvUrl(SHEET_CONFIG.MAIN_SHEET, {
          gid: SHEET_CONFIG.MAIN_SHEET_GID,
          bustCache: true,
        })
      );

      if (!response.ok) {
        throw new Error('Sheet fetch failed');
      }

      const csvText = await response.text();
      const rows = parseCSV(csvText);

      if (!isMountedRef.current) return;

      if (!rows || rows.length === 0) {
        setHeaders([]);
        setData([]);
        return;
      }

      const sheetHeaders = rows[0] || [];
      const dataRows = buildInventoryAssetRows(rows, sheetHeaders);

      setHeaders(
        sheetHeaders.filter((header) => String(header || '').trim() !== '')
      );

      setData(dataRows);
    } catch (err) {
      console.error('Inventory data fetch error:', err);

      if (isMountedRef.current) {
        setError('Failed to load inventory. Make sure the Google Sheet is public.');
        setHeaders([]);
        setData([]);
      }
    } finally {
      isFetchingRef.current = false;

      if (isMountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    fetchData({ silent: true });

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchData]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!document.hidden) {
        fetchData({ silent: true });
      }
    }, SHEET_CONFIG.REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [fetchData]);

  return {
    data,
    headers,
    loading,
    refreshing,
    error,
    refreshData: () => fetchData({ silent: false }),
  };
};